<section class="studio-bridge">
  <header class="studio-bridge__header">
    <div>
      <h2 class="studio-bridge__title">
        {{ 'app.patrol.roblox.cockpit.studio.title' | translate }}
      </h2>
      <p class="studio-bridge__hint">
        {{ 'app.patrol.roblox.cockpit.studio.hint' | translate }}
      </p>
    </div>
  </header>

  <section class="studio-bridge__credentials">
    <h3 class="studio-bridge__credentials-title">
      {{ 'app.patrol.roblox.cockpit.studio.credentialsTitle' | translate }}
    </h3>
    <p class="studio-bridge__credentials-hint">
      {{ 'app.patrol.roblox.cockpit.studio.credentialsHint' | translate }}
    </p>

    <div class="studio-bridge__credential-row">
      <div class="studio-bridge__credential-copy">
        <strong>{{ 'app.patrol.roblox.cockpit.studio.ingestSecretTitle' | translate }}</strong>
        <p>
          @if (hasIngestSecret()) {
            {{ 'app.patrol.roblox.cockpit.studio.ingestSecretConfigured' | translate }}
          } @else {
            {{ 'app.patrol.roblox.cockpit.studio.ingestSecretMissing' | translate }}
          }
        </p>
      </div>
      <app-cta-button
        class="studio-bridge__credential-cta"
        variant="light"
        size="small"
        [loading]="isGeneratingSecret()"
        [disabled]="isGeneratingSecret()"
        (clicked)="generateIngestSecret()"
      >
        <span cta-label>{{
          isGeneratingSecret()
            ? ('app.patrol.roblox.cockpit.studio.generatingSecret' | translate)
            : (hasIngestSecret()
              ? ('app.patrol.roblox.cockpit.studio.rotateIngestSecret' | translate)
              : ('app.patrol.roblox.cockpit.studio.generateIngestSecret' | translate))
        }}</span>
      </app-cta-button>
    </div>

    @if (ingestSecretShownOnce(); as secret) {
      <div class="studio-bridge__secret-once" role="status">
        <p>{{ 'app.patrol.roblox.cockpit.studio.ingestSecretShownOnce' | translate }}</p>
        <code>{{ secret }}</code>
        <app-cta-button
          variant="plain"
          size="small"
          [minHeightDisabled]="true"
          (clicked)="copyIngestSecret()"
        >
          <span cta-label>
            @if (copiedIngestSecret()) {
              {{ 'app.patrol.roblox.cockpit.studio.copied' | translate }}
            } @else {
              {{ 'app.patrol.roblox.cockpit.studio.copySecret' | translate }}
            }
          </span>
        </app-cta-button>
      </div>
    }

    <div class="studio-bridge__api-key">
      <div class="studio-bridge__credential-row">
        <div class="studio-bridge__credential-copy">
          <strong>{{ 'app.patrol.roblox.cockpit.studio.openCloudKeyLabel' | translate }}</strong>
          <p>
            @if (hasOpenCloudApiKey()) {
              {{ 'app.patrol.roblox.cockpit.studio.openCloudKeyConfigured' | translate }}
            } @else {
              {{ 'app.patrol.roblox.cockpit.studio.openCloudKeyHint' | translate }}
            }
          </p>
        </div>
        @if (hasOpenCloudApiKey() && !showOpenCloudKeyForm()) {
          <app-cta-button
            class="studio-bridge__credential-cta"
            variant="light"
            size="small"
            (clicked)="startReplacingOpenCloudApiKey()"
          >
            <span cta-label>{{
              'app.patrol.roblox.cockpit.studio.replaceOpenCloudKey' | translate
            }}</span>
          </app-cta-button>
        }
      </div>

      @if (showOpenCloudKeyForm()) {
        <div class="studio-bridge__credential-row">
          <label class="studio-bridge__field studio-bridge__field--api-key studio-bridge__field--grow">
            <input
              type="password"
              name="openCloudApiKey"
              autocomplete="off"
              [ngModel]="openCloudApiKey()"
              (ngModelChange)="openCloudApiKey.set($event)"
              [placeholder]="
                (hasOpenCloudApiKey()
                  ? 'app.patrol.roblox.cockpit.studio.openCloudKeyReplacePlaceholder'
                  : 'app.patrol.roblox.cockpit.studio.openCloudKeyPlaceholder'
                ) | translate
              "
              [attr.aria-label]="'app.patrol.roblox.cockpit.studio.openCloudKeyLabel' | translate"
            />
          </label>
          <app-cta-button
            class="studio-bridge__credential-cta"
            variant="light"
            size="small"
            [loading]="isSavingOpenCloudKey()"
            [disabled]="!openCloudApiKey().trim() || isSavingOpenCloudKey()"
            (clicked)="saveOpenCloudApiKey()"
          >
            <span cta-label>{{
              isSavingOpenCloudKey()
                ? ('app.patrol.roblox.cockpit.studio.savingOpenCloudKey' | translate)
                : (hasOpenCloudApiKey()
                  ? ('app.patrol.roblox.cockpit.studio.updateOpenCloudKey' | translate)
                  : ('app.patrol.roblox.cockpit.studio.saveOpenCloudKey' | translate))
            }}</span>
          </app-cta-button>
          @if (hasOpenCloudApiKey()) {
            <app-cta-button
              variant="plain"
              size="small"
              [minHeightDisabled]="true"
              (clicked)="cancelReplacingOpenCloudApiKey()"
            >
              <span cta-label>{{
                'app.patrol.roblox.cockpit.studio.cancelReplaceOpenCloudKey' | translate
              }}</span>
            </app-cta-button>
          }
        </div>
      }
    </div>

    @if (credentialsError(); as credError) {
      <p class="studio-bridge__error" role="alert">
        {{ 'app.patrol.roblox.cockpit.studio.errors.' + credError | translate }}
      </p>
    }
  </section>

  <form class="studio-bridge__form" (submit)="$event.preventDefault(); generateScript()">
    <label class="studio-bridge__field">
      <span>{{ 'app.patrol.roblox.cockpit.studio.channelLabel' | translate }}</span>
      <input
        type="text"
        name="channelName"
        [ngModel]="channelName()"
        (ngModelChange)="channelName.set($event)"
        [placeholder]="'app.patrol.roblox.cockpit.studio.channelPlaceholder' | translate"
        maxlength="256"
      />
      <span class="studio-bridge__field-hint">
        {{ 'app.patrol.roblox.cockpit.studio.channelHint' | translate }}
      </span>
    </label>

    <label class="studio-bridge__field studio-bridge__field--number">
      <span>{{ 'app.patrol.roblox.cockpit.studio.warnMuteLabel' | translate }}</span>
      <input
        type="number"
        name="warnMuteSeconds"
        min="0"
        max="3600"
        [ngModel]="warnMuteSeconds()"
        (ngModelChange)="warnMuteSeconds.set($event)"
      />
      <span class="studio-bridge__field-hint">
        {{ 'app.patrol.roblox.cockpit.studio.warnMuteHint' | translate }}
      </span>
    </label>

    <label class="studio-bridge__field studio-bridge__field--checkbox">
      <input
        type="checkbox"
        name="blockOnDecision"
        [ngModel]="blockOnDecision()"
        (ngModelChange)="blockOnDecision.set($event)"
      />
      <span>{{ 'app.patrol.roblox.cockpit.studio.blockOnDecision' | translate }}</span>
    </label>

    <label class="studio-bridge__field studio-bridge__field--number">
      <span>{{ 'app.patrol.roblox.cockpit.studio.moderationTimeoutLabel' | translate }}</span>
      <input
        type="number"
        name="moderationTimeoutSeconds"
        min="2"
        max="15"
        step="1"
        inputmode="numeric"
        [ngModel]="moderationTimeoutSeconds()"
        (ngModelChange)="moderationTimeoutSeconds.set(+$event || 4)"
      />
      <small class="studio-bridge__field-hint">
        {{ 'app.patrol.roblox.cockpit.studio.moderationTimeoutHint' | translate }}
      </small>
    </label>

    <label class="studio-bridge__field studio-bridge__field--checkbox">
      <input
        type="checkbox"
        name="failClosedOnOutage"
        [ngModel]="failClosedOnOutage()"
        (ngModelChange)="failClosedOnOutage.set($event)"
      />
      <span>{{ 'app.patrol.roblox.cockpit.studio.failClosedOnOutage' | translate }}</span>
    </label>

    <div class="studio-bridge__form-actions">
      <button
        type="submit"
        class="studio-bridge__generate-btn"
        [disabled]="!canGenerate() || isGenerating()"
      >
        {{
          isGenerating()
            ? ('app.patrol.roblox.cockpit.studio.generating' | translate)
            : ('app.patrol.roblox.cockpit.studio.generate' | translate)
        }}
      </button>
    </div>
  </form>

  @if (generateError(); as errorKey) {
    <p class="studio-bridge__error" role="alert">
      {{ 'app.patrol.roblox.cockpit.studio.errors.' + errorKey | translate }}
    </p>
  }

  <div class="studio-bridge__output">
    <div class="studio-bridge__output-header">
      <h3>{{ 'app.patrol.roblox.cockpit.studio.scriptTitle' | translate }}</h3>
    </div>

    @if (scriptEndpoints(); as endpoints) {
      <dl class="studio-bridge__endpoints">
        <div class="studio-bridge__endpoint-row">
          <dt>{{ 'app.patrol.roblox.cockpit.studio.endpoints.linkPage' | translate }}</dt>
          <dd><code>{{ endpoints.linkPageBaseUrl }}</code></dd>
        </div>
        <div class="studio-bridge__endpoint-row">
          <dt>{{ 'app.patrol.roblox.cockpit.studio.endpoints.linkStart' | translate }}</dt>
          <dd><code>{{ endpoints.linkStartUrl }}</code></dd>
        </div>
        <div class="studio-bridge__endpoint-row">
          <dt>{{ 'app.patrol.roblox.cockpit.studio.endpoints.ingest' | translate }}</dt>
          <dd><code>{{ endpoints.valvurUrl }}</code></dd>
        </div>
      </dl>
    }

    @if (activeFile(); as file) {
      <div class="code-panel" role="region" [attr.aria-label]="file.fileName">
        <div class="code-panel__chrome">
          <div class="code-panel__traffic" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div class="code-panel__tabs" role="tablist">
            @for (scriptFile of scriptFiles(); track scriptFile.id) {
              <div
                class="code-panel__tab-item"
                [class.code-panel__tab-item--active]="scriptFile.id === activeFileId()"
              >
                <button
                  type="button"
                  class="code-panel__tab"
                  role="tab"
                  [class.code-panel__tab--active]="scriptFile.id === activeFileId()"
                  [attr.aria-selected]="scriptFile.id === activeFileId()"
                  (click)="selectFile(scriptFile)"
                >
                  {{ scriptFile.fileName }}
                </button>
                <button
                  type="button"
                  class="code-panel__tab-copy"
                  [attr.aria-label]="scriptFile.fileName + ' — ' + (
                    copiedScriptFileId() === scriptFile.id
                      ? ('app.patrol.roblox.cockpit.studio.copied' | translate)
                      : ('app.patrol.roblox.cockpit.studio.copy' | translate)
                  )"
                  (click)="copyScriptFile(scriptFile)"
                >
                  @if (copiedScriptFileId() === scriptFile.id) {
                    {{ 'app.patrol.roblox.cockpit.studio.copied' | translate }}
                  } @else {
                    {{ 'app.patrol.roblox.cockpit.studio.copy' | translate }}
                  }
                </button>
              </div>
            }
          </div>
        </div>

        <div class="code-panel__viewport">
          <div class="code-panel__gutter" aria-hidden="true">
            @for (line of activeLineNumbers(); track line) {
              <span class="code-panel__line-number">{{ line }}</span>
            }
          </div>
          <pre class="code-panel__code"><code>{{ file.content }}</code></pre>
        </div>

        <div class="code-panel__statusbar">
          <span class="code-panel__statusbar-path">{{ file.location }}</span>
          <span class="code-panel__statusbar-meta">Lua · UTF-8</span>
        </div>
      </div>

      <p class="studio-bridge__install">
        {{ 'app.patrol.roblox.cockpit.studio.installHint' | translate }}
      </p>
    } @else {
      <div class="code-panel code-panel--empty">
        <div class="code-panel__chrome">
          <div class="code-panel__traffic" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <span class="code-panel__empty-title">ValvurPatrolBridge.server.lua</span>
        </div>
        <p class="studio-bridge__placeholder">
          {{ 'app.patrol.roblox.cockpit.studio.secretRequired' | translate }}
        </p>
      </div>
    }
  </div>
</section>
